"use client";

import { useAnalytics } from "@/lib/analytics";

import type { CtaLocation, OutboundDestination } from "@/lib/analytics";
import type { AnchorHTMLAttributes, MouseEvent } from "react";

interface TrackedLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
	href: string;
	/** Marketing surface this link sits on. */
	location: CtaLocation;
	/** Overrides the destination inferred from the URL. */
	destination?: OutboundDestination;
	linkText?: string;
}

/** Best-effort mapping from a URL to a known destination bucket. */
export function inferDestination(href: string): OutboundDestination {
	if (href.startsWith("mailto:")) {
		return "email";
	}
	if (href.includes("github.com")) {
		return "github";
	}
	if (href.includes("discord.gg") || href.includes("discord.com")) {
		return "discord";
	}
	if (href.includes("x.com") || href.includes("twitter.com")) {
		return "x";
	}
	if (href.includes("docs.")) {
		return "docs";
	}
	return "external";
}

/**
 * Anchor that emits `outbound_link_clicked`. Use for any link leaving the
 * marketing site so docs/GitHub/community traffic is attributable to a surface.
 */
export function TrackedLink({
	href,
	location,
	destination,
	linkText,
	onClick,
	...props
}: TrackedLinkProps) {
	const { capture } = useAnalytics();

	const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
		capture("outbound_link_clicked", {
			destination: destination ?? inferDestination(href),
			link_url: href,
			link_text: linkText ?? event.currentTarget.innerText,
			location,
		});
		onClick?.(event);
	};

	return <a {...props} href={href} onClick={handleClick} />;
}
